import React, { useState, useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import {
  DataTable,
  Button,
  Checkbox,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "lift-ui";
import type { TableColumn } from "lift-ui";
import { Eye, ExternalLink, Trash2 } from "lucide-react";
import {
  PantryHeader,
  UploadPantryDialog,
  ViewPantryDialog,
  ConfirmDialog,
  ReorderPantryDialog,
  type FileWithMetadata,
} from "./components";
import { PantryItem, getAllPantryItems, uploadPantryFiles, deletePantryItem, bulkDeletePantryItems } from "./api";
import { sortByLatestDate } from "../../../shared/utils/sortUtils";
import "./pantry.styles.scss";

interface PantryProps {
  hideHeader?: boolean;
  onHandlersReady?: (handlers: {
    onUploadClick: () => void;
    onReorder: () => void;
    searchTerm: string;
    onSearch: (term: string) => void;
    selectedCount: number;
    onBulkDelete: () => void;
    onClearSelection: () => void;
  }) => void;
  hasEditAccess?: boolean;
  hasManageAccess?: boolean;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const Pantry: React.FC<PantryProps> = ({ hideHeader = false, onHandlersReady, hasEditAccess = true, hasManageAccess = true }) => {
  const [pantryItems, setPantryItems] = useState<PantryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<PantryItem | null>(null);
  const [itemToDelete, setItemToDelete] = useState<PantryItem | null>(null);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  useEffect(() => {
    fetchPantryItems();
  }, []);

  const fetchPantryItems = async () => {
    try {
      setLoading(true);
      const items = await getAllPantryItems();
      // Sort pantry items by most recent created_at or updated_at first
      const sortedItems = sortByLatestDate(items);
      setPantryItems(sortedItems);
    } catch (error) {
      console.error("Error fetching pantry items:", error);
      setPantryItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (filesWithMetadata: FileWithMetadata[]) => {
    try {
      console.log('🚀 Starting upload process...', filesWithMetadata);
      
      // Call the API
      const uploadResponse = await uploadPantryFiles(filesWithMetadata);
      console.log('📥 Upload response received:', uploadResponse);
      
      // After successful upload, refresh the entire list from server
      console.log('🔄 Refreshing pantry items from server...');
      await fetchPantryItems();
      
      // Clear any existing selections
      setSelectedItems([]);
      
      console.log('✅ Upload process completed successfully');
      
    } catch (error) {
      console.error("❌ Error uploading files:", error);
      // On error, also refresh to ensure correct state
      await fetchPantryItems();
      setSelectedItems([]);
      throw error; // Re-throw to let dialog handle the error
    }
  };

  const handleViewItem = (item: PantryItem) => {
    if (item.source_url) {
      // Open the source URL (GCP link) in a new tab
      window.open(item.source_url, '_blank', 'noopener,noreferrer');
    } else {
      // Fallback to showing the view dialog if no source URL
      setSelectedItem(item);
      setViewDialogOpen(true);
    }
  };

  const handleDeleteClick = (item: PantryItem) => {
    setItemToDelete(item);
    setConfirmDialogOpen(true);
  };

  const handleSelectItem = (itemId: number) => {
    setSelectedItems(prev => 
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = (selected: boolean) => {
    setSelectedItems(selected ? pantryItems.map(item => item.id) : []);
  };

  const handleClearSelection = () => {
    setSelectedItems([]);
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialogOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    try {
      // Optimistically remove selected items from UI (maintain sorting)
      setPantryItems(prevItems => {
        const filtered = prevItems.filter(item => !selectedItems.includes(item.id));
        // Re-sort after filtering to maintain order
        return sortByLatestDate(filtered);
      });
      
      // Close dialog and clear selection immediately
      setBulkDeleteDialogOpen(false);
      const itemsToDelete = [...selectedItems];
      setSelectedItems([]);
      
      // Make the API call
      await bulkDeletePantryItems(itemsToDelete);
      
      console.log(`Successfully bulk deleted ${itemsToDelete.length} pantry items`);
      
    } catch (error) {
      console.error("Error bulk deleting pantry items:", error);
      
      // On error, refetch to restore correct state
      await fetchPantryItems();
      setSelectedItems([]);
    }
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      // Optimistically update the UI by removing the item immediately (maintain sorting)
      const itemToDeleteId = itemToDelete.id;
      setPantryItems(prevItems => {
        const filtered = prevItems.filter(item => item.id !== itemToDeleteId);
        // Re-sort after filtering to maintain order
        return sortByLatestDate(filtered);
      });
      
      // Clear selection if deleted item was selected
      setSelectedItems(prev => prev.filter(id => id !== itemToDeleteId));
      
      // Close dialog immediately for better UX
      setConfirmDialogOpen(false);
      setItemToDelete(null);
      
      // Make the API call
      await deletePantryItem(itemToDeleteId);
      
      // Success - UI is already updated optimistically
      console.log(`Successfully deleted pantry item ${itemToDeleteId}`);
      
    } catch (error) {
      console.error("Error deleting pantry item:", error);
      
      // On error, refetch to restore correct state
      await fetchPantryItems();
    }
  };

  // Expose handlers to parent
  useEffect(() => {
    if (onHandlersReady) {
      onHandlersReady({
        onUploadClick: () => setUploadDialogOpen(true),
        onReorder: () => {
          setOrderDialogOpen(true);
        },
        searchTerm,
        onSearch: setSearchTerm,
        selectedCount: selectedItems.length,
        onBulkDelete: handleBulkDelete,
        onClearSelection: handleClearSelection,
      });
    }
  }, [onHandlersReady, searchTerm, selectedItems.length]);

  const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();
  const truncateText = (text: string | null, maxLength = 50) =>
    !text ? "N/A" : text.length > maxLength ? text.substring(0, maxLength) + "..." : text;

  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) return pantryItems;
    const term = searchTerm.trim().toLowerCase();
    return pantryItems.filter(
      (item) =>
        (item.file_name && item.file_name.toLowerCase().includes(term)) ||
        (item.metadata?.title && String(item.metadata.title).toLowerCase().includes(term)) ||
        (item.metadata?.description && String(item.metadata.description).toLowerCase().includes(term))
    );
  }, [pantryItems, searchTerm]);

  const paginatedItems = useMemo(
    () => filteredItems.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [filteredItems, currentPage, itemsPerPage]
  );
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const selectAllOnPage = (selected: boolean) => {
    if (selected) {
      setSelectedItems((prev) => [...new Set([...prev, ...paginatedItems.map((i) => i.id)])]);
    } else {
      const pageIds = new Set(paginatedItems.map((i) => i.id));
      setSelectedItems((prev) => prev.filter((id) => !pageIds.has(id)));
    }
  };
  const isAllSelectedOnPage = paginatedItems.length > 0 && paginatedItems.every((i) => selectedItems.includes(i.id));

  const pantryColumns: TableColumn<PantryItem>[] = useMemo(() => {
    const cols: TableColumn<PantryItem>[] = [];
    if (hasManageAccess) {
      cols.push({
        key: "select",
        header: (
          <Checkbox
            checked={isAllSelectedOnPage}
            onCheckedChange={(c) => selectAllOnPage(!!c)}
            aria-label="Select all on page"
          />
        ),
        accessor: (row) => row.id,
        width: "50px",
        render: (_value, row) => (
          <Checkbox
            checked={selectedItems.includes(row.id)}
            onCheckedChange={() => handleSelectItem(row.id)}
            aria-label={`Select ${row.file_name}`}
          />
        ),
      });
    }
    cols.push(
      {
        key: "file_name",
        header: "FILE NAME",
        accessor: "file_name",
        width: "18%",
        render: (value) => <span className="font-medium">{value}</span>,
      },
      {
        key: "title",
        header: "TITLE",
        accessor: (row) => row.metadata?.title ?? "",
        width: "18%",
        render: (value) => truncateText(value || null),
      },
      {
        key: "description",
        header: "DESCRIPTION",
        accessor: (row) => row.metadata?.description ?? "",
        width: "22%",
        render: (value) => truncateText(value || null),
      },
      {
        key: "sort_order",
        header: "ORDER",
        accessor: "sort_order",
        width: "8%",
        align: "center",
        render: (value) => String(value),
      },
      {
        key: "created_at",
        header: "CREATED",
        accessor: "created_at",
        width: "12%",
        render: (value) => formatDate(value),
      }
    );
    cols.push({
      key: "actions",
      header: "ACTIONS",
      accessor: (row) => row.id,
      width: "auto",
      align: "center",
      render: (_value, row) => (
        <div className="flex items-center justify-center gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleViewItem(row)}
                  className="h-8 w-8 p-0"
                >
                  {row.source_url ? (
                    <ExternalLink className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{row.source_url ? "Open link" : "View"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {hasManageAccess && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(row)}
                    className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Delete</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    });
    return cols;
  }, [hasManageAccess, selectedItems, isAllSelectedOnPage, paginatedItems]);

  return (
    <div className="pantry-container flex flex-col flex-1 min-h-0">
      {!hideHeader && (
        <PantryHeader 
          onUploadClick={() => setUploadDialogOpen(true)}
          selectedCount={selectedItems.length}
          onBulkDelete={handleBulkDelete}
          onClearSelection={handleClearSelection}
          pantryItems={pantryItems}
          onReorderComplete={fetchPantryItems}
          hasEditAccess={hasEditAccess}
          hasManageAccess={hasManageAccess}
        />
      )}

      <div className="flex-1 min-h-0">
        <DataTable<PantryItem>
          className="flex-1 min-h-0 overflow-auto"
          maxHeight="calc(100vh - 200px)"
          columns={pantryColumns}
          data={paginatedItems}
          loading={loading}
          emptyMessage="No pantry items found. Upload files to get started."
          stickyHeader
          stickyFooter
          pagination={{
            currentPage,
            totalPages,
            pageSize: itemsPerPage,
            totalItems: filteredItems.length,
            onPageChange: setCurrentPage,
            onPageSizeChange: (size) => {
              setItemsPerPage(size);
              setCurrentPage(1);
            },
            pageSizeOptions: PAGE_SIZE_OPTIONS,
          }}
          showActions={false}
        />
      </div>

      {/* Dialogs */}
      <UploadPantryDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUpload={handleUpload}
      />

      <ViewPantryDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedItem(null);
        }}
        item={selectedItem}
      />

      <ConfirmDialog
        open={confirmDialogOpen}
        title="Delete Pantry Item"
        message={`Are you sure you want to delete "${itemToDelete?.file_name}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        onClose={() => {
          setConfirmDialogOpen(false);
          setItemToDelete(null);
        }}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmColor="destructive"
      />

      <ConfirmDialog
        open={bulkDeleteDialogOpen}
        title="Delete Selected Items"
        message={`Are you sure you want to delete ${selectedItems.length} selected item${selectedItems.length !== 1 ? 's' : ''}? This action cannot be undone.`}
        onConfirm={confirmBulkDelete}
        onClose={() => {
          setBulkDeleteDialogOpen(false);
        }}
        confirmLabel={`Delete ${selectedItems.length} Item${selectedItems.length !== 1 ? 's' : ''}`}
        cancelLabel="Cancel"
        confirmColor="destructive"
      />

      <ReorderPantryDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        pantryItems={pantryItems}
        onReorderComplete={fetchPantryItems}
      />
    </div>
  );
};

export default Pantry;
